import { useEffect, useRef, useState } from 'react'

/**
 * Returns a ref and a 0–1 progress value representing how far
 * the element has scrolled through the viewport.
 * Drives CSS custom property --parallax-y on the element for
 * pure-CSS parallax consumers that prefer not to use inline styles.
 *
 * @param {object} options
 * @param {number} options.speed   — multiplier (default 0.4). Positive = moves up slower.
 * @param {boolean} options.clamp  — clamp translateY to ± 40px (default true)
 */
export function useParallax({ speed = 0.4, clamp = true } = {}) {
  const ref = useRef(null)
  const frameRef = useRef(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    // Skip if user prefers reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const update = () => {
      const rect = el.getBoundingClientRect()
      const vh   = window.innerHeight

      // progress: 0 when bottom of element hits bottom of viewport,
      //           1 when top of element hits top of viewport
      const progress = 1 - (rect.bottom / (vh + rect.height))
      const shift    = progress * rect.height * speed
      const clamped  = clamp ? Math.max(-40, Math.min(40, shift)) : shift

      el.style.setProperty('--parallax-y', `${clamped}px`)
    }

    const onScroll = () => {
      if (frameRef.current) return
      frameRef.current = requestAnimationFrame(() => {
        update()
        frameRef.current = null
      })
    }

    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', update, { passive: true })

    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', update)
      if (frameRef.current) cancelAnimationFrame(frameRef.current)
    }
  }, [speed, clamp])

  return ref
}

/**
 * Tracks window scroll position (0–1 normalised to document height).
 * Useful for progress indicators or orchestrating multi-element scenes.
 */
export function useScrollProgress() {
  const [progress, setProgress] = useState(0)
  const frameRef = useRef(null)

  useEffect(() => {
    const update = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight
      setProgress(max > 0 ? window.scrollY / max : 0)
    }

    const onScroll = () => {
      if (frameRef.current) return
      frameRef.current = requestAnimationFrame(() => {
        update()
        frameRef.current = null
      })
    }

    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (frameRef.current) cancelAnimationFrame(frameRef.current)
    }
  }, [])

  return progress
}
